import type { ProductVariant } from '../types/pz';
import { formatCatalogProductDimensions } from './productDimensions';
import { sanitizeProductDisplayName } from './productDisplayNameSanitizer';

export { sanitizeProductDisplayName } from './productDisplayNameSanitizer';

/**
 * Product selectors are an operator-facing surface. Technical product codes,
 * SKUs and compatibility ids may be searchable, but must never become the
 * visible fallback label.
 */
export const getProductSelectorName = (
  variant: ProductVariant | undefined,
  unnamedProductLabel: string,
) => {
  if (!variant) return unnamedProductLabel;
  const product = variant.product;
  const technicalValues = [
    variant.id,
    variant.productId,
    variant.sku,
    variant.ean,
    product?.id,
    product?.code,
    product?.ean,
  ];

  for (const candidate of [product?.name, product?.englishName, variant.variantName]) {
    const displayName = sanitizeProductDisplayName(candidate, technicalValues);
    if (displayName) return displayName;
  }

  return unnamedProductLabel;
};

export const getProductSelectorDimensions = (variant?: ProductVariant) => (
  variant ? formatCatalogProductDimensions(variant.product, variant) : ''
);

export const getProductSelectorEan = (variant?: ProductVariant) => (
  variant?.ean || variant?.product?.ean || ''
);

export const isRealProductVariant = (variant?: ProductVariant) => {
  if (!variant) return false;
  if (variant.isDefault !== undefined) return variant.isDefault === false;
  return Boolean(variant.variantName && variant.variantName !== 'Standard');
};

type ProductVariantAttributeSource = Pick<ProductVariant, 'attributes'>;

export const getProductVariantAttributeEntries = (variant?: ProductVariantAttributeSource) => (
  Object.entries(variant?.attributes || {})
    .map(([key, value]) => [String(key).trim(), String(value).trim()] as const)
    .filter(([key, value]) => Boolean(key && value))
    .sort(([left], [right]) => left.localeCompare(right, 'pl'))
);

export const getProductVariantName = (variant?: ProductVariant) => {
  if (!isRealProductVariant(variant)) return '';

  const attributeLabel = getProductVariantAttributeEntries(variant)
    .map(([key, value]) => `${key}: ${value}`)
    .join(' · ');

  return attributeLabel || String(variant?.variantName || '').trim();
};

export const getProductVariantDisplayName = (
  variant: ProductVariant | undefined,
  basicProductLabel: string,
) => (
  isRealProductVariant(variant)
    ? getProductVariantName(variant)
    : basicProductLabel
);

export const getProductSelectionLabel = (
  variant: ProductVariant | undefined,
  unnamedProductLabel: string,
) => {
  const productName = getProductSelectorName(variant, unnamedProductLabel);
  const variantName = getProductVariantName(variant);
  return [productName, variantName].filter(Boolean).join(' — ');
};

const DIMENSION_FIELDS = ['thickness', 'width', 'height', 'length'] as const;

// Everything except the dimension values: names, codes, attributes.
const getNonDimensionSearchParts = (variant: ProductVariant) => [
  variant.sku,
  variant.ean,
  variant.product?.ean,
  variant.product?.code,
  variant.product?.name,
  variant.product?.englishName,
  variant.product?.productCategoryName,
  variant.product?.productCategoryParentName,
  variant.variantName,
  ...Object.entries(variant.attributes || {}).flatMap(([key, value]) => [key, value]),
];

/** Codes stay useful for lookup without leaking into the rendered option. */
export const buildProductSelectorSearchText = (variant: ProductVariant) => [
  ...getNonDimensionSearchParts(variant),
  variant.width,
  variant.height,
  variant.length,
  variant.thickness,
  variant.product?.width,
  variant.product?.height,
  variant.product?.length,
  variant.product?.thickness,
  getProductSelectorDimensions(variant),
].filter(Boolean).join(' ').toLowerCase();

const NUMERIC_TOKEN = /^\d+(?:[.,]\d+)?$/;
// Shorter numbers are dimensions; longer ones are codes (EAN, SKU) that
// operators also type from the middle or the end.
const CODE_LIKE_DIGITS = 5;

const toDecimalPoint = (value: string) => value.replace(',', '.');

// One group per dimension field (the variant and product values of the same
// field are alternatives), so one dimension answers one typed number only.
const getDimensionValueGroups = (variant: ProductVariant) => DIMENSION_FIELDS
  .map((field) => Array.from(new Set(
    [variant[field], variant.product?.[field]]
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0)
      .map(String),
  )))
  .filter((group) => group.length > 0);

// Operators type dimensions as "20 x 125" while the label reads
// "gr. 20 x szer. 1250 x dł. 2500 mm": split the query into tokens, drop the
// "x"/"×"/"*" separators between numbers and match numbers as value prefixes.
// Mirror note: the product list uses the stricter exact-value grammar in
// backend/src/utils/productDimensionSearch.js.
export const matchesProductSelectorSearch = (variant: ProductVariant, query: string) => {
  const tokens = query
    .toLowerCase()
    .replace(/(\d)\s*[x×*]\s*(?=\d|$)/g, '$1 ')
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return false;

  const text = buildProductSelectorSearchText(variant);
  const nonDimensionText = getNonDimensionSearchParts(variant).filter(Boolean).join(' ').toLowerCase();
  const nonDimensionNumbers = (nonDimensionText.match(/\d+(?:[.,]\d+)?/g) || []).map(toDecimalPoint);
  const freeDimensions = getDimensionValueGroups(variant);

  // Longest numbers first so "20 x 2000" cannot spend the 2000 mm side on "20".
  const orderedTokens = [...tokens].sort((left, right) => right.length - left.length);
  return orderedTokens.every((token) => {
    if (!NUMERIC_TOKEN.test(token)) return text.includes(token);

    const number = toDecimalPoint(token);
    const dimensionIndex = freeDimensions.findIndex((group) => group.some((value) => value.startsWith(number)));
    if (dimensionIndex >= 0) {
      freeDimensions.splice(dimensionIndex, 1);
      return true;
    }
    return nonDimensionNumbers.some((candidate) => candidate.startsWith(number))
      || (number.replace('.', '').length >= CODE_LIKE_DIGITS && nonDimensionText.includes(token));
  });
};
